// Shared formatting + category/discount helpers for KoupoNow web.

export const BRAND = "#E63946";

export function euro(n) {
  const v = Number(n || 0);
  return v.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

export function distanceLabel(km) {
  if (km === null || km === undefined) return "";
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(1)} km`;
}

// Human label for the discount, per discount_type.
export function discountLabel(offer) {
  const t = offer.discount_type || "percent";
  const v = offer.discount_value ?? offer.discount ?? 0;
  if (t === "percent") return `-${Math.round(v)}%`;
  if (t === "fixed") return `-${euro(v)}`;
  if (t === "price") return euro(v);
  if (t === "bogo") return "2x1";
  return offer.deal_label || `-${Math.round(v)}%`;
}

export function discountLong(offer) {
  const t = offer.discount_type || "percent";
  const v = offer.discount_value ?? offer.discount ?? 0;
  if (t === "percent") return `Sconto del ${Math.round(v)}%`;
  if (t === "fixed") return `${euro(v)} di sconto`;
  if (t === "price") return `Prezzo speciale ${euro(v)}`;
  if (t === "bogo") return offer.deal_label || "Prendi 2 paghi 1";
  return offer.deal_label || "Offerta speciale";
}

export function timeLeft(expiresAt) {
  if (!expiresAt) return "";
  const end = new Date(expiresAt).getTime();
  const now = Date.now();
  let diff = Math.max(0, end - now);
  if (diff <= 0) return "Scaduta";
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  if (h >= 24) {
    const d = Math.floor(h / 24);
    return `${d}g`;
  }
  if (h >= 1) return `${h}h ${m}m`;
  return `${m}m`;
}

export function relativeDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export const DAY_NAMES = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

export function daysRangeLabel(days) {
  const s = [...days].sort((a, b) => a - b);
  if (s.length === 7) return "Tutti i giorni";
  const contiguous = s.every((v, i) => i === 0 || v === s[i - 1] + 1);
  if (contiguous && s.length >= 3) return `${DAY_NAMES[s[0]]}–${DAY_NAMES[s[s.length - 1]]}`;
  return s.map((d) => DAY_NAMES[d]).join(", ");
}

// True if expiry is effectively "no end" (recurring perpetual = > ~1 year away).
export function isPerpetual(expires) {
  if (!expires) return true;
  return new Date(expires).getTime() - Date.now() > 365 * 86400000;
}

const hhLabel = (t) => { if (!t) return ""; const [h, m] = t.split(":"); return m === "00" ? h : `${h}:${m}`; };
const ddmm = (iso) => { const d = new Date(iso); return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`; };

// Short human availability/urgency phrase for an offer (client-facing).
export function availabilityLabel(o) {
  if (!o) return "";
  if (o.recurring) {
    const days = o.recur_days && o.recur_days.length ? daysRangeLabel(o.recur_days) : "Tutti i giorni";
    const time = o.recur_start && o.recur_end ? ` ${hhLabel(o.recur_start)}–${hhLabel(o.recur_end)}` : "";
    let s = `Disponibile ${days}${time}`;
    if (o.expires_at && !isPerpetual(o.expires_at)) s += ` · fino al ${ddmm(o.expires_at)}`;
    return s;
  }
  const now = new Date();
  const starts = o.starts_at ? new Date(o.starts_at) : null;
  if (starts && starts > now) {
    return o.expires_at ? `Dal ${ddmm(o.starts_at)} al ${ddmm(o.expires_at)}` : `Dal ${ddmm(o.starts_at)}`;
  }
  if (o.expires_at) {
    const exp = new Date(o.expires_at);
    const win = o.daily_start && o.daily_end ? ` · solo ${hhLabel(o.daily_start)}–${hhLabel(o.daily_end)}` : "";
    if (exp.toDateString() === now.toDateString()) return `Valida solo oggi${win}`;
    return `Valida fino al ${ddmm(o.expires_at)}${win}`;
  }
  return "";
}

// Compact urgency badges for offer cards (client feed).
export function offerBadges(o) {
  if (!o) return [];
  const out = [];
  if (o.recurring) {
    const days = o.recur_days && o.recur_days.length ? daysRangeLabel(o.recur_days) : "";
    const time = o.recur_start && o.recur_end ? `${hhLabel(o.recur_start)}–${hhLabel(o.recur_end)}` : "";
    const label = [days, time].filter(Boolean).join(" ");
    if (label) out.push({ key: "recur", type: "clock", label });
  } else {
    const now = new Date();
    const starts = o.starts_at ? new Date(o.starts_at) : null;
    if (starts && starts > now) out.push({ key: "starts", type: "clock", label: `Dal ${ddmm(o.starts_at)}` });
    else if (o.expires_at) {
      const exp = new Date(o.expires_at);
      if (exp.toDateString() === now.toDateString()) out.push({ key: "today", type: "clock", label: "Solo oggi" });
      else out.push({ key: "until", type: "clock", label: `Fino al ${ddmm(o.expires_at)}` });
    }
    if (o.daily_start && o.daily_end) out.push({ key: "window", type: "clock", label: `${hhLabel(o.daily_start)}–${hhLabel(o.daily_end)}` });
  }
  const rem = o.remaining ?? (o.quantity ? Math.max(0, o.quantity - (o.click_count || 0)) : null);
  if (o.quantity && rem !== null && rem !== undefined) {
    out.push({ key: "qty", type: "tag", label: `${rem} ${rem === 1 ? "rimasto" : "rimasti"}`, warn: true });
  }
  return out;
}

// Fallback icon (lucide name) per macro category id.
export const CATEGORY_FALLBACK_IMG =
  "https://images.unsplash.com/photo-1513104890138-7c749659a591?w=800&q=70";
